import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { CalendarClock, Ban } from "lucide-react";
import { useState, useMemo } from "react";

// Jours : clé "0"=Lundi … "6"=Dimanche (aligné sur date.weekday() côté backend Python).
const DAYS = [["0", "Lundi"], ["1", "Mardi"], ["2", "Mercredi"], ["3", "Jeudi"], ["4", "Vendredi"], ["5", "Samedi"], ["6", "Dimanche"]];
const DAY_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DEFAULT_DAY = { closed: false, open: "09:00", close: "19:00", break_start: "12:30", break_end: "14:00" };
const pad = (n) => String(n).padStart(2, "0");
const toMin = (s) => { const [h, m] = (s || "0:0").split(":").map(Number); return h * 60 + m; };

// Génère les créneaux "HH:MM" d'une journée (miroir du backend generate_day_slots).
function genDaySlots(conf, slotMin) {
  if (!conf || conf.closed) return [];
  const open = toMin(conf.open || "09:00"), close = toMin(conf.close || "19:00");
  const bs = conf.break_start ? toMin(conf.break_start) : null;
  const be = conf.break_end ? toMin(conf.break_end) : null;
  const out = []; let t = open;
  while (t + slotMin <= close) {
    const overlap = bs != null && be != null && t < be && t + slotMin > bs;
    if (!overlap) out.push(`${pad(Math.floor(t / 60))}:${pad(t % 60)}`);
    t += slotMin;
  }
  return out;
}

export default function OpeningHoursSettings({ settings, onChange }) {
  const hours = settings.opening_hours && Object.keys(settings.opening_hours).length ? settings.opening_hours : {};
  const slotMin = settings.slot_minutes || 60;
  const blocked = Array.isArray(settings.blocked_slots) ? settings.blocked_slots : [];

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [selDate, setSelDate] = useState(null);

  const dayConf = (k) => ({ ...DEFAULT_DAY, ...(hours[k] || {}) });
  const setDay = (k, patch) => onChange("opening_hours", { ...hours, [k]: { ...dayConf(k), ...patch } });

  // Config d'un jour calendaire (clé "0"=Lundi via (getDay()+6)%7).
  const confForDate = (d) => { const [y, m, da] = d.split("-").map(Number); const wd = String((new Date(y, m - 1, da).getDay() + 6) % 7); return dayConf(wd); };
  const isFullBlocked = (d) => blocked.some((b) => b.date === d && !b.time);
  const isSlotBlocked = (d, t) => blocked.some((b) => b.date === d && b.time === t);
  const someBlocked = (d) => blocked.some((b) => b.date === d);

  const toggleFullDay = (d) => {
    if (isFullBlocked(d)) onChange("blocked_slots", blocked.filter((b) => !(b.date === d && !b.time)));
    else onChange("blocked_slots", [...blocked.filter((b) => b.date !== d), { date: d }]); // remplace les blocages du jour
  };
  const toggleSlot = (d, t) => {
    if (isSlotBlocked(d, t)) onChange("blocked_slots", blocked.filter((b) => !(b.date === d && b.time === t)));
    else onChange("blocked_slots", [...blocked, { date: d, time: t }]);
  };

  const prevMonth = () => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); };

  const grid = useMemo(() => {
    const startOffset = (new Date(year, month - 1, 1).getDay() + 6) % 7;
    const nDays = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= nDays; d++) cells.push(`${year}-${pad(month)}-${pad(d)}`);
    return cells;
  }, [year, month]);

  const timeInput = (val, oc) => (
    <input type="time" value={val || ""} onChange={(e) => oc(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-sm" />
  );

  const selConf = selDate ? confForDate(selDate) : null;
  const selSlots = selConf ? genDaySlots(selConf, slotMin) : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Horaires & prise de rendez-vous</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Durée des créneaux */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Durée d'un créneau</label>
          <select value={slotMin} onChange={(e) => onChange("slot_minutes", parseInt(e.target.value, 10))} className="rounded-md border border-gray-300 px-2 py-1 text-sm">
            <option value={30}>30 minutes</option>
            <option value={60}>1 heure</option>
          </select>
        </div>

        {/* Horaires par jour */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-800">Horaires d'ouverture</p>
          {DAYS.map(([k, label]) => {
            const c = dayConf(k);
            return (
              <div key={k} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 p-2">
                <span className="w-24 text-sm font-medium text-gray-700">{label}</span>
                <label className="flex items-center gap-1 text-sm text-gray-600">
                  <input type="checkbox" checked={!!c.closed} onChange={(e) => setDay(k, { closed: e.target.checked })} /> Fermé
                </label>
                {!c.closed && (
                  <div className="flex flex-wrap items-center gap-1.5 text-sm text-gray-600">
                    <span>de</span>{timeInput(c.open, (v) => setDay(k, { open: v }))}
                    <span>à</span>{timeInput(c.close, (v) => setDay(k, { close: v }))}
                    <span className="ml-2">pause</span>{timeInput(c.break_start, (v) => setDay(k, { break_start: v }))}
                    <span>–</span>{timeInput(c.break_end, (v) => setDay(k, { break_end: v }))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Calendrier de blocage */}
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><Ban className="h-4 w-4" /> Bloquer des dates / créneaux</p>
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Calendrier */}
            <div className="rounded-xl border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <button type="button" onClick={prevMonth} className="h-8 w-8 rounded-full border border-gray-200 text-lg font-bold hover:bg-gray-50">‹</button>
                <span className="text-sm font-bold text-gray-800">{MONTHS[month - 1]} {year}</span>
                <button type="button" onClick={nextMonth} className="h-8 w-8 rounded-full border border-gray-200 text-lg font-bold hover:bg-gray-50">›</button>
              </div>
              <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-400">
                {DAY_SHORT.map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1">
                {grid.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const conf = confForDate(d);
                  const closed = conf.closed;
                  const full = isFullBlocked(d);
                  const partial = !full && blocked.some((b) => b.date === d && b.time);
                  const sel = selDate === d;
                  const cls = sel ? "bg-gray-800 text-white"
                    : full ? "bg-red-500 text-white"
                    : partial ? "bg-amber-400 text-white"
                    : closed ? "bg-gray-100 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700";
                  return (
                    <button key={d} type="button" onClick={() => setSelDate(d)} className={`aspect-square rounded-lg text-sm font-semibold transition ${cls}`}>
                      {parseInt(d.slice(-2))}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-gray-500">
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Journée bloquée</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Créneaux bloqués</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-gray-200" /> Fermé</span>
              </div>
            </div>

            {/* Détail du jour sélectionné */}
            <div className="rounded-xl border border-gray-200 p-3">
              {!selDate ? (
                <p className="text-sm text-gray-400">Cliquez sur un jour pour le bloquer ou bloquer certains créneaux.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-gray-800">{selDate.slice(-2)} {MONTHS[month - 1]} {year}</p>
                    <button type="button" onClick={() => toggleFullDay(selDate)}
                      className={`rounded-md px-3 py-1 text-xs font-semibold ${isFullBlocked(selDate) ? "bg-red-500 text-white" : "border border-red-300 text-red-600 hover:bg-red-50"}`}>
                      {isFullBlocked(selDate) ? "Journée bloquée ✕" : "Bloquer la journée"}
                    </button>
                  </div>
                  {selConf.closed ? (
                    <p className="mt-3 text-sm text-gray-400">Fermé ce jour (selon vos horaires).</p>
                  ) : isFullBlocked(selDate) ? (
                    <p className="mt-3 text-sm text-red-500">Journée entièrement bloquée.</p>
                  ) : (
                    <>
                      <p className="mt-3 text-xs text-gray-500">Touchez un créneau pour le bloquer / débloquer :</p>
                      <div className="mt-2 grid grid-cols-3 gap-1.5 sm:grid-cols-4">
                        {selSlots.map((t) => {
                          const b = isSlotBlocked(selDate, t);
                          return (
                            <button key={t} type="button" onClick={() => toggleSlot(selDate, t)}
                              className={`rounded-md border py-1.5 text-sm font-semibold ${b ? "border-red-400 bg-red-500 text-white line-through" : "border-gray-200 text-gray-700 hover:border-gray-400"}`}>{t}</button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-400">N'oubliez pas de cliquer « Enregistrer » en bas de page pour appliquer horaires et blocages.</p>
      </CardContent>
    </Card>
  );
}
